import { BufferAttribute, BufferGeometry, DoubleSide, Ray, Vector3 } from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import type { TriangleSoup } from './design-region-bounds';

/** How much of a part's surface sits on a region thinner than the threshold. */
export interface ThinRegionMeasure {
  /** Surface area over thin regions, both faces counted, in mm². */
  thinAreaMm2: number;
  /** Median thickness over that area, in mm; null when nothing is thin. */
  thicknessMm: number | null;
  /** The part's whole surface area, in mm². */
  surfaceAreaMm2: number;
}

/** The design parts to find thin regions on, as world-space triangles. */
export interface ThinRegionsRequest {
  parts: { id: string; soup: TriangleSoup }[];
  thresholdMm: number;
}

/** A design part's thin pieces, and whether the thin-design warning names it. */
export interface ThinRegionSolid {
  id: string;
  /** The thin pieces of its triangles, 9 numbers per triangle, in the request's frame. */
  positions: Float32Array;
  named: boolean;
}

/** Messages from the thin-regions worker. */
export type ThinRegionsMessage =
  | { type: 'progress'; done: number; total: number }
  | { type: 'done'; solids: ThinRegionSolid[] }
  | { type: 'error'; message: string };

// The optimizer's smallest feature is the larger of the minimum feature size
// and its density filter, 3 to 6 voxel edges depending on the analysis
// (OptimCon::SetFilterWidth). The widest filter is taken, so no analysis
// type is missed.
const OPTIMIZER_FILTER_VOXELS = 6;
// Thin surface area, in squared thresholds, before a part is reported: a
// patch one threshold across, counted on both of its faces. Smaller thin
// spots (chamfer tips, fillet edges) are left out.
const THIN_DESIGN_MIN_AREA_FACTOR = 2;
// Share of a part's surface that must be thin before the part is named: a
// part that is mostly a thin plate, not a bulky part with thin walls around
// its holes or along its edges. A 0.1" plate under a bulky body is 32 % thin
// at Low and 21 % at Medium.
const THIN_DESIGN_MIN_SHARE = 0.15;
// Samples per part for the warning; a part needing more is sampled evenly
// and the area scaled up.
const THIN_DESIGN_MAX_SAMPLES = 20000;
// Samples per part for the overlay, which paints every sample it measures.
const THIN_OVERLAY_MAX_SAMPLES = 400000;
// Triangles are cut into pieces no longer than this share of the threshold,
// so a long CAD triangle is not judged by its centre alone.
const SAMPLE_EDGE_FACTOR = 0.5;
// Most pieces a triangle is cut into along each edge.
const MAX_SPLITS = 64;
// The face a thickness ray exits through must lie within 60° of facing it
// head on; a grazing exit is a corner, not an opposite wall.
const THIN_DESIGN_MIN_EXIT_COS = 0.5;
// Samples measured between progress reports.
const PROGRESS_EVERY = 2000;

/**
 * The thickness below which a design region is thin: the optimizer's
 * smallest feature, the larger of the minimum feature size and 6 voxel edges.
 * @param featureSizeMm - Minimum feature size in mm (project.minimumFeatureSize)
 * @param voxelEdgeMm - Longest voxel edge of the optimizer's grid in mm
 * @returns Threshold in mm; 0 when neither input is usable
 */
export function thinDesignThresholdMm(featureSizeMm: number, voxelEdgeMm: number): number {
  const filterMm = Number.isFinite(voxelEdgeMm) && voxelEdgeMm > 0 ? OPTIMIZER_FILTER_VOXELS * voxelEdgeMm : 0;
  return Math.max(featureSizeMm > 0 ? featureSizeMm : 0, filterMm);
}

/**
 * Whether the thin-design warning names a part: its thin surface is at least
 * a patch one threshold across, on both faces, and at least 15 % of the part.
 */
export function isThinDesignPart(thinAreaMm2: number, surfaceAreaMm2: number, thresholdMm: number): boolean {
  return (
    thinAreaMm2 > 0 &&
    thinAreaMm2 >= THIN_DESIGN_MIN_AREA_FACTOR * thresholdMm * thresholdMm &&
    thinAreaMm2 >= THIN_DESIGN_MIN_SHARE * surfaceAreaMm2
  );
}

/** The total area of a part's triangles, in mm². */
function surfaceArea(soup: TriangleSoup): number {
  let sum = 0;
  for (let i = 0; i + 9 <= soup.length; i += 9) {
    const ux = soup[i + 3] - soup[i];
    const uy = soup[i + 4] - soup[i + 1];
    const uz = soup[i + 5] - soup[i + 2];
    const vx = soup[i + 6] - soup[i];
    const vy = soup[i + 7] - soup[i + 1];
    const vz = soup[i + 8] - soup[i + 2];
    sum += Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx) / 2;
  }
  return sum;
}

/**
 * The surface ray crossings along `ray`, nearest first, past the ray's own
 * start. Hits closer than `eps` are one crossing -- a ray through a shared
 * edge or vertex hits every triangle there.
 */
function surfaceCrossings(bvh: MeshBVH, ray: Ray, eps: number): { distance: number; normal: Vector3 }[] {
  const hits = bvh
    .raycast(ray, DoubleSide)
    .filter((hit) => hit.distance > eps && hit.face)
    .sort((a, b) => a.distance - b.distance);
  const crossings: { distance: number; normal: Vector3 }[] = [];
  for (const hit of hits) {
    const last = crossings[crossings.length - 1];
    if (!last || hit.distance - last.distance > eps) crossings.push({ distance: hit.distance, normal: hit.face!.normal });
  }
  return crossings;
}

/** How a part's triangles are cut into samples, and which samples are measured. */
interface SamplePlan {
  /** Pieces along each edge, per triangle; a triangle is cut into its square. */
  splits: Uint8Array;
  /** Pieces over all triangles. */
  total: number;
  /** Measures every stride-th piece. */
  stride: number;
}

function longestEdge(soup: TriangleSoup, i: number): number {
  const edge = (p: number, q: number): number =>
    Math.hypot(soup[q] - soup[p], soup[q + 1] - soup[p + 1], soup[q + 2] - soup[p + 2]);
  return Math.max(edge(i, i + 3), edge(i + 3, i + 6), edge(i + 6, i));
}

/**
 * Cuts each triangle into pieces no longer than half the threshold; when that
 * makes more than `maxSamples` pieces, the pieces grow until it does not, and
 * past one piece per triangle the pieces are sampled evenly.
 */
function planSamples(soup: TriangleSoup, thresholdMm: number, maxSamples: number): SamplePlan {
  const triangles = Math.floor(soup.length / 9);
  const longest = new Float32Array(triangles);
  for (let t = 0; t < triangles; t++) longest[t] = longestEdge(soup, t * 9);

  const splits = new Uint8Array(triangles);
  let pieceMm = thresholdMm * SAMPLE_EDGE_FACTOR;
  let total = 0;
  for (let attempt = 0; attempt < 8; attempt++) {
    total = 0;
    for (let t = 0; t < triangles; t++) {
      const n = Math.min(MAX_SPLITS, Math.max(1, Math.ceil(longest[t] / pieceMm)));
      splits[t] = n;
      total += n * n;
    }
    if (total <= maxSamples || total === triangles) break;
    pieceMm *= Math.sqrt(total / maxSamples) * 1.05;
  }
  return { splits, total, stride: Math.max(1, Math.ceil(total / Math.max(1, maxSamples))) };
}

/**
 * Calls `visit` for every measured piece of the part's triangles thinner than
 * `thresholdMm`. From the centre of each piece a ray runs into the solid along
 * its normal; the distance to the opposite wall is the thickness there. The
 * inward side is the one whose ray crosses the surface an odd number of
 * times, so the triangles' winding does not matter. A piece whose two rays do
 * not settle which side is inside (an open mesh), or whose ray leaves through
 * a grazing face, is not measured.
 *
 * Gaps narrower than the threshold -- holes, slots -- are too small for the
 * optimizer to form, so they count as solid: a piece facing into one is not
 * measured, and a ray that crosses one goes on through the material beyond.
 * The wall between a bolt hole and the part's face is therefore not thin.
 * @param soup - The part's triangles, 9 numbers per triangle
 * @param thresholdMm - Thickness below which a region counts as thin
 * @param plan - How the triangles are cut and sampled
 * @param visit - Gets the piece's corners (reused between calls), its thickness and its area
 * @param onProgress - Gets the pieces gone through so far and the total
 */
function forEachThinPiece(
  soup: TriangleSoup,
  thresholdMm: number,
  plan: SamplePlan,
  visit: (a: Vector3, b: Vector3, c: Vector3, thicknessMm: number, areaMm2: number) => void,
  onProgress?: (done: number, total: number) => void
): void {
  const triangles = Math.floor(soup.length / 9);
  if (triangles === 0 || !(thresholdMm > 0)) return;

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(soup, 3));
  geometry.computeBoundingBox();
  const diagonal = geometry.boundingBox!.getSize(new Vector3()).length();
  const eps = Math.max(diagonal * 1e-6, 1e-9);
  const bvh = new MeshBVH(geometry);

  const origin = new Vector3();
  const u = new Vector3();
  const v = new Vector3();
  const pa = new Vector3();
  const pb = new Vector3();
  const pc = new Vector3();
  const edge = new Vector3();
  const normal = new Vector3();
  const ray = new Ray();
  const corner = (i: number, j: number, n: number, out: Vector3): Vector3 =>
    out.copy(origin).addScaledVector(u, i / n).addScaledVector(v, j / n);

  const measure = (): void => {
    normal.subVectors(pb, pa).cross(edge.subVectors(pc, pa));
    const doubleArea = normal.length();
    if (doubleArea <= 0) return;
    normal.divideScalar(doubleArea);
    ray.origin.copy(pa).add(pb).add(pc).divideScalar(3);

    ray.direction.copy(normal);
    const along = surfaceCrossings(bvh, ray, eps);
    ray.direction.negate();
    const against = surfaceCrossings(bvh, ray, eps);
    const alongInward = along.length % 2 === 1;
    if (alongInward === (against.length % 2 === 1)) return;

    const inwardDirection = alongInward ? normal : ray.direction;
    const inward = alongInward ? along : against;
    const outward = alongInward ? against : along;
    if (outward.length > 0 && outward[0].distance < thresholdMm) return;
    // Crossings alternate exit, entry, exit...; a narrow gap is stepped over.
    let k = 0;
    while (k + 2 < inward.length && inward[k + 1].distance - inward[k].distance < thresholdMm) k += 2;
    const exit = inward[k];
    if (exit.distance >= thresholdMm) return;
    if (Math.abs(exit.normal.dot(inwardDirection)) < THIN_DESIGN_MIN_EXIT_COS) return;
    visit(pa, pb, pc, exit.distance, (doubleArea / 2) * plan.stride);
  };

  let piece = 0;
  for (let t = 0; t < triangles; t++) {
    const i = t * 9;
    const n = plan.splits[t];
    origin.set(soup[i], soup[i + 1], soup[i + 2]);
    u.set(soup[i + 3], soup[i + 4], soup[i + 5]).sub(origin);
    v.set(soup[i + 6], soup[i + 7], soup[i + 8]).sub(origin);
    // n² pieces on a barycentric grid: n(n+1)/2 upright, n(n-1)/2 upside down.
    for (let a = 0; a < n; a++) {
      for (let b = 0; a + b < n; b++) {
        for (let flipped = 0; flipped < (a + b < n - 1 ? 2 : 1); flipped++) {
          if (onProgress && piece % PROGRESS_EVERY === 0) onProgress(piece, plan.total);
          if (piece++ % plan.stride !== 0) continue;
          if (flipped === 0) {
            corner(a, b, n, pa);
            corner(a + 1, b, n, pb);
            corner(a, b + 1, n, pc);
          } else {
            corner(a + 1, b, n, pa);
            corner(a + 1, b + 1, n, pb);
            corner(a, b + 1, n, pc);
          }
          measure();
        }
      }
    }
  }
  onProgress?.(plan.total, plan.total);
  geometry.dispose();
}

/**
 * Measures where a closed mesh is thinner than `thresholdMm` (see
 * {@link forEachThinPiece}).
 * @param soup - The part's triangles, 9 numbers per triangle
 * @param thresholdMm - Thickness below which a region counts as thin
 * @param maxSamples - Most rays to cast each way; a denser mesh is sampled evenly
 * @returns Thin surface area and its median thickness
 */
export function measureThinRegion(
  soup: TriangleSoup,
  thresholdMm: number,
  maxSamples = THIN_DESIGN_MAX_SAMPLES
): ThinRegionMeasure {
  const surfaceAreaMm2 = surfaceArea(soup);
  if (!(thresholdMm > 0)) return { thinAreaMm2: 0, thicknessMm: null, surfaceAreaMm2 };
  const thin: { thickness: number; area: number }[] = [];
  forEachThinPiece(soup, thresholdMm, planSamples(soup, thresholdMm, maxSamples), (_a, _b, _c, thickness, area) =>
    thin.push({ thickness, area })
  );
  if (thin.length === 0) return { thinAreaMm2: 0, thicknessMm: null, surfaceAreaMm2 };

  const thinAreaMm2 = thin.reduce((sum, sample) => sum + sample.area, 0);
  thin.sort((x, y) => x.thickness - y.thickness);
  let covered = 0;
  const median = thin.find((sample) => (covered += sample.area) >= thinAreaMm2 / 2) ?? thin[thin.length - 1];
  return { thinAreaMm2, thicknessMm: median.thickness, surfaceAreaMm2 };
}

/**
 * The thin pieces of every design part, measured as the thin-design warning
 * measures them, more densely, and whether the warning names the part (see
 * {@link isThinDesignPart}).
 * @param request - The design parts and the threshold
 * @param onProgress - Gets the pieces gone through so far over all parts, and the total
 * @returns One solid per part with thin pieces
 */
export function thinRegionSolids(
  request: ThinRegionsRequest,
  onProgress?: (done: number, total: number) => void
): ThinRegionSolid[] {
  const { parts, thresholdMm } = request;
  if (!(thresholdMm > 0)) return [];
  const plans = parts.map((part) => planSamples(part.soup, thresholdMm, THIN_OVERLAY_MAX_SAMPLES));
  const total = plans.reduce((sum, plan) => sum + plan.total, 0);
  const solids: ThinRegionSolid[] = [];
  let before = 0;

  parts.forEach(({ id, soup }, p) => {
    const thin: number[] = [];
    let area = 0;
    forEachThinPiece(
      soup,
      thresholdMm,
      plans[p],
      (a, b, c, _, pieceArea) => {
        thin.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
        area += pieceArea;
      },
      onProgress && ((done) => onProgress(before + done, total))
    );
    before += plans[p].total;
    if (thin.length === 0) return;
    const named = isThinDesignPart(area, surfaceArea(soup), thresholdMm);
    solids.push({ id, positions: new Float32Array(thin), named });
  });
  return solids;
}
